import React from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Modal from '@lib/components/Modal/Modal';
import { IconName, ModalIconColor } from '@lib/components/Modal/enums';
import Typography from '@lib/components/Typography/Typography';
import styles from './FeatureLockHelpPopup.module.scss';

export type FeatureLockHelpItem = {
  title: React.ReactNode;
  description?: React.ReactNode;
};

export type FeatureLockHelpSection = {
  chipLabel: React.ReactNode;
  intro?: FeatureLockHelpItem;
  items: FeatureLockHelpItem[];
};

export type FeatureLockHelpPopupContent = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  sections: FeatureLockHelpSection[];
  upgradeButtonText?: string;
};

type Props = FeatureLockHelpPopupContent & {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
};

function HelpItem({ title, description }: FeatureLockHelpItem) {
  return (
    <li className={styles.item}>
      <Typography variant="body" strong className={styles.itemTitle}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body" className={styles.itemDescription}>
          {description}
        </Typography>
      )}
    </li>
  );
}

function HelpSection({ chipLabel, intro, items }: FeatureLockHelpSection) {
  return (
    <div className={styles.section}>
      <span className={styles.chip}>
        <Typography variant="label" className={styles.chipLabel}>
          {chipLabel}
        </Typography>
      </span>
      {intro && (
        <div className={styles.intro}>
          <Typography variant="body" strong className={styles.itemTitle}>
            {intro.title}
          </Typography>
          {intro.description && (
            <Typography variant="body" className={styles.itemDescription}>
              {intro.description}
            </Typography>
          )}
        </div>
      )}
      <ul className={styles.itemList}>
        {items.map((item, index) => (
          <HelpItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            title={item.title}
            description={item.description}
          />
        ))}
      </ul>
    </div>
  );
}

export default function FeatureLockHelpPopup({
  isOpen,
  onClose,
  onUpgrade,
  title,
  subtitle,
  sections,
  upgradeButtonText,
}: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon={IconName.Help}
      iconColor={ModalIconColor.Blue}
      title={title}
      description={subtitle}
      actions={
        <Button
          buttonText={upgradeButtonText || t('upgrade-plan')}
          buttonType={ButtonTypes.primaryFilled}
          fullWidth
          onClick={() => {
            onClose();
            onUpgrade();
          }}
        />
      }
    >
      <div className={styles.sections}>
        {sections.map((section, index) => (
          <HelpSection
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            chipLabel={section.chipLabel}
            intro={section.intro}
            items={section.items}
          />
        ))}
      </div>
    </Modal>
  );
}
